'use strict';

/**
 * Pict-Provider-StickerAPI
 * ------------------------
 * Browser-side client for the moodboard sticker library. Stickers are small transparent cutouts
 * (PNG or SVG) with bytes in Parime; the two-stage upload mirrors MediaAPI. The library merges the
 * system built-in set (IDCustomer 0) with the caller's own customer stickers; the server scopes the
 * customer side and sanitizes uploaded SVG.
 *
 *   listLibrary()                 -> Promise<sticker[]>  (built-in + customer rows, server-merged)
 *   upload(file, overrides)       -> Promise<{ IDSticker, ... }>  (POST /New then PUT /:id/Blob)
 *   blobURL(id)                   -> the GET blob URL for a sticker (used as the card image src)
 *   remove(id)                    -> soft-delete a customer sticker (customer-scoped CRUD)
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'StickerAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderStickerAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	blobURL(pIDSticker) { return this._url('Sticker/' + pIDSticker + '/Blob'); }

	/** The merged library: system built-ins (IDCustomer 0) plus the caller's customer stickers. */
	listLibrary()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('StickerLibrary'))
			.then((pBody) => (pBody && Array.isArray(pBody.Stickers)) ? pBody.Stickers : [])
			.catch(() => []);
	}

	/**
	 * Two-stage upload: POST metadata to /Sticker/New, then PUT the bytes to /Sticker/:id/Blob.
	 * pFile is a browser File/Blob; pOverrides can set Name / Category / MimeType / OriginalFileName.
	 * Only PNG and SVG are accepted server-side.
	 */
	upload(pFile, pOverrides)
	{
		let tmpOverrides = pOverrides || {};
		let tmpMime = tmpOverrides.MimeType || pFile.type || '';
		let tmpMeta =
		{
			Name: tmpOverrides.Name || pFile.name || 'Sticker',
			MimeType: tmpMime,
			OriginalFileName: tmpOverrides.OriginalFileName || pFile.name || 'sticker',
			FileSizeBytes: pFile.size || pFile.byteLength || 0,
			Category: tmpOverrides.Category || ''
		};

		return libFrameworkClient.postJSON(this.fable, this._url('Sticker/New'), tmpMeta)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.Sticker)
				{
					throw new Error((pBody && pBody.Error) || 'Could not register sticker.');
				}
				let tmpID = pBody.Sticker.IDSticker;
				return this._putBlob(tmpID, pFile, tmpMime).then(() => Object.assign({ IDSticker: tmpID }, pBody.Sticker));
			});
	}

	_putBlob(pIDSticker, pFile, pMimeType)
	{
		let tmpBlob = (pFile instanceof ArrayBuffer) ? new Blob([pFile], { type: pMimeType }) : pFile;
		return libFrameworkClient.uploadBinary(this.fable, this._url('Sticker/' + pIDSticker + '/Blob'), tmpBlob, pMimeType || 'application/octet-stream', 'PUT');
	}

	/** Soft-delete a customer sticker via the customer-scoped CRUD (never touches a built-in). */
	remove(pIDSticker)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('Sticker/' + pIDSticker))
			.then(() => true);
	}
}

module.exports = PictProviderStickerAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
